import React, { memo } from 'react';
import { Col, Row } from 'components/Grid/Grid';
import { Text } from 'components/Typography';
import { FontWeights } from 'core/CssVariables';
import { useNavigate } from 'react-router';
import { RoutesPaths } from 'routes/Routes.types';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { setUserTimeZone } from 'redux/slices';
import { useChangeUserTimezoneMutation } from 'redux/apiSlices/user.slices';
import { timeEntryApi } from 'redux/apiSlices/timeEntry.slice';
import { ticketsApi } from 'redux/apiSlices/ticket.slice';
import { tasksApi } from 'redux/apiSlices/task.slices';
import { projectApi } from 'redux/apiSlices/project.slice';
import { useTimezoneDetection } from 'hooks/useTimezoneDetection';
import { getTimezoneDisplayName } from 'helpers/timezoneUtils';
import { SHeaderAlert } from 'components/Layout/HeaderAlert/HeaderAlert.style';
import Button from 'components/Button/Button';
import { TButtonTypes } from 'components/Button/Button.types';
import { toast } from 'components/Notification';
import { TRtkErrorType } from 'data/types/generalDataTypes';

const TimezoneAlert = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(state => state.authSlice.user);
  const [changeTimezone, { isLoading }] = useChangeUserTimezoneMutation();

  const { browserTimezone, userTimezone, showPrompt, dismissPrompt } =
    useTimezoneDetection();

  const handleUpdateTimezone = async () => {
    if (!user?.id || !browserTimezone) return;

    try {
      await changeTimezone({
        resourceId: user.id,
        timeZone: browserTimezone,
      }).unwrap();

      dispatch(setUserTimeZone(browserTimezone));

      // Invalidate timesheet and ticket cache to refetch with new timezone
      dispatch(
        timeEntryApi.util.invalidateTags([
          'TimeSheetInfo',
          'TimeSheetPerAssignment',
          'TimeTracker',
        ]),
      );
      dispatch(ticketsApi.util.invalidateTags(['Tickets', 'Ticket']));
      dispatch(tasksApi.util.invalidateTags(['Tasks', 'Task']));
      dispatch(projectApi.util.invalidateTags(['projectsCompact', 'project']));

      dismissPrompt();

      toast({
        description: `Timezone updated to ${getTimezoneDisplayName(
          browserTimezone,
        )}`,
        title: 'Timezone Updated',
        type: 'success',
      });
    } catch (error) {
      toast({
        description:
          (error as TRtkErrorType)?.data?.message ||
          'Failed to update timezone',
        title: 'Update Timezone',
        type: 'error',
      });
    }
  };

  const handleGoToSettings = () => {
    dismissPrompt();
    navigate(`/app/${RoutesPaths.Profile}`);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    // Wrapped like the payment and card-expiry alerts: bare in the flex row it
    // would size to its content and stop short of the full width.
    <Col xs={24}>
      <SHeaderAlert
        type="info"
        message={
          <Row role="row" align="middle">
            <Text
              fontlevel={5}
              fontWeight={FontWeights.Regular}
              className="mr-2"
            >
              Your browser timezone ({getTimezoneDisplayName(browserTimezone)})
              differs from your saved timezone (
              {getTimezoneDisplayName(userTimezone || '')}).
            </Text>
            <Button
              size="small"
              className="mr-2"
              loading={isLoading}
              onClick={handleUpdateTimezone}
            >
              Update to {browserTimezone}
            </Button>
            <Button
              size="small"
              outline
              color={TButtonTypes.Default}
              className="mr-2"
              onClick={handleGoToSettings}
            >
              Go to Settings
            </Button>
            <Text
              className="pointer"
              fontlevel={5}
              fontWeight={FontWeights.Regular}
              underline
              onClick={dismissPrompt}
            >
              Dismiss
            </Text>
          </Row>
        }
      />
    </Col>
  );
};

export default memo(TimezoneAlert);
